import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { 
  GroupAdd as GroupAddIcon,
  ArrowBack as ArrowBackIcon,
  Check as CheckIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Person as PersonIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

interface User {
  firstName: string;
  lastName: string;
  email: string;
  role: string;
}

const AddUsersSetup: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: 'user',
  });
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const signupData = JSON.parse(sessionStorage.getItem('signupData') || '{}');
  const selectedPlan = signupData.selectedPlan || { name: 'Free' };
  const domain = signupData.domain || 'example.com';

  useEffect(() => {
    if (!signupData.organization) {
      navigate('/signup');
    }
  }, [signupData, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleAddUser = () => {
    if (!formData.firstName || !formData.lastName || !formData.email) {
      setError('Please fill in all fields');
      return;
    }
    
    const fullEmail = `${formData.email}@${domain}`;
    if (users.some(u => u.email === fullEmail)) {
      setError('This email address already exists');
      return;
    }

    setUsers([...users, { ...formData, email: fullEmail }]);
    setFormData({ firstName: '', lastName: '', email: '', role: 'user' });
    setSuccess('User added to list');
    setTimeout(() => setSuccess(''), 2000);
  };

  const handleRemoveUser = (index: number) => {
    setUsers(users.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (users.length === 0) {
      navigate('/dashboard');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API_URL}/signup/add-users`, {
        organizationId: signupData.organizationId,
        domainId: signupData.domainId,
        users: users,
      });

      if (response.data.success || response.data.data) {
        setSuccess('Users created successfully!');
        setTimeout(() => navigate('/dashboard'), 1000);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create users');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    navigate('/dashboard');
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#ffffff', display: 'flex', flexDirection: 'column', fontFamily: 'Inter, sans-serif' }}>
      {/* Header */}
      <Box sx={{ borderBottom: '1px solid #e5e7eb', py: 2, px: 3 }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => navigate('/setup/complete')} sx={{ color: '#64748b' }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#dc2626', fontFamily: 'Inter, sans-serif' }}>
              CruvzMail
            </Typography>
          </Box>
          <Button component={RouterLink} to="/login" sx={{ color: '#dc2626', fontWeight: 600, textTransform: 'none' }}>
            Sign In
          </Button>
        </Box>
      </Box>

      {/* Header Banner */}
      <Box sx={{ bgcolor: '#f8fafc', py: 3, borderBottom: '1px solid #e5e7eb' }}>
        <Box sx={{ maxWidth: 1000, mx: 'auto', px: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ width: 50, height: 50, borderRadius: '50%', bgcolor: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <GroupAddIcon sx={{ fontSize: 24, color: '#dc2626' }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#111827', fontFamily: 'Inter, sans-serif' }}>
              Add Team Members
            </Typography>
            <Typography sx={{ color: '#6b7280', fontFamily: 'Inter, sans-serif' }}>
              Invite users to join your organization
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ flex: 1, py: 6 }}>
        <Box sx={{ maxWidth: 1000, mx: 'auto', px: 3, display: 'flex', gap: 6 }}>
          {/* Left - Form */}
          <Box sx={{ flex: 1 }}>
            {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>{success}</Alert>}

            <Box sx={{ bgcolor: '#f8fafc', borderRadius: 3, p: 4, mb: 4, border: '1px solid #e5e7eb' }}>
              <Typography sx={{ fontWeight: 600, color: '#111827', mb: 3, fontFamily: 'Inter, sans-serif' }}>
                Add New User
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <TextField
                  fullWidth
                  name="firstName"
                  placeholder="First name"
                  value={formData.firstName}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><PersonIcon sx={{ color: '#9ca3af' }} /></InputAdornment>,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2, bgcolor: '#ffffff',
                      '& fieldset': { borderColor: '#e5e7eb' },
                      '&:hover fieldset': { borderColor: '#dc2626' },
                      '&.Mui-focused fieldset': { borderColor: '#dc2626' },
                    },
                  }}
                />
                <TextField
                  fullWidth
                  name="lastName"
                  placeholder="Last name"
                  value={formData.lastName}
                  onChange={handleChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2, bgcolor: '#ffffff',
                      '& fieldset': { borderColor: '#e5e7eb' },
                      '&:hover fieldset': { borderColor: '#dc2626' },
                      '&.Mui-focused fieldset': { borderColor: '#dc2626' },
                    },
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <TextField
                  name="email"
                  placeholder="username"
                  value={formData.email}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><EmailIcon sx={{ color: '#9ca3af' }} /></InputAdornment>,
                  }}
                  sx={{
                    flex: 1,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px 0 0 8px', bgcolor: '#ffffff',
                      '& fieldset': { borderColor: '#e5e7eb' },
                      '&:hover fieldset': { borderColor: '#dc2626' },
                      '&.Mui-focused fieldset': { borderColor: '#dc2626' },
                    },
                  }}
                />
                <Box sx={{ px: 2, py: 1.85, bgcolor: '#f3f4f6', border: '1px solid #e5e7eb', borderLeft: 'none', borderRadius: '0 8px 8px 0' }}>
                  <Typography sx={{ color: '#6b7280', fontWeight: 500, fontFamily: 'Inter, sans-serif' }}>
                    @{domain}
                  </Typography>
                </Box>
              </Box>

              <Button
                fullWidth
                variant="contained"
                onClick={handleAddUser}
                startIcon={<AddIcon />}
                sx={{ py: 1.5, bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' }, fontWeight: 600, textTransform: 'none', borderRadius: 2 }}
              >
                Add to List
              </Button>
            </Box>

            {/* Users List */}
            {users.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography sx={{ fontWeight: 600, color: '#111827', mb: 2, fontFamily: 'Inter, sans-serif' }}>
                  Users to Create ({users.length})
                </Typography>
                <TableContainer sx={{ bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e5e7eb' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f1f5f9' }}>
                        <TableCell sx={{ fontWeight: 600, color: '#374151', fontFamily: 'Inter, sans-serif' }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#374151', fontFamily: 'Inter, sans-serif' }}>Email</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#374151', fontFamily: 'Inter, sans-serif' }}>Role</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#374151', fontFamily: 'Inter, sans-serif' }}></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {users.map((user, index) => (
                        <TableRow key={index}>
                          <TableCell sx={{ fontFamily: 'Inter, sans-serif' }}>{user.firstName} {user.lastName}</TableCell>
                          <TableCell sx={{ fontFamily: 'Inter, sans-serif' }}>{user.email}</TableCell>
                          <TableCell>
                            <Chip label={user.role} size="small" sx={{ bgcolor: '#dbeafe', color: '#1e40af', fontFamily: 'Inter, sans-serif' }} />
                          </TableCell>
                          <TableCell>
                            <IconButton onClick={() => handleRemoveUser(index)} size="small" sx={{ color: '#ef4444' }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={handleSkip}
                sx={{ flex: 1, py: 1.5, borderColor: '#e5e7eb', color: '#6b7280', '&:hover': { borderColor: '#dc2626', bgcolor: 'transparent' }, fontWeight: 600, textTransform: 'none', borderRadius: 2 }}
              >
                Skip for Now
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading}
                sx={{ flex: 1, py: 1.5, bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' }, fontWeight: 600, textTransform: 'none', borderRadius: 2 }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : users.length > 0 ? `Create ${users.length} Users` : 'Continue'}
              </Button>
            </Box>
          </Box>

          {/* Right - Info Panel */}
          <Box sx={{ width: 300, display: { xs: 'none', md: 'block' } }}>
            <Box sx={{ bgcolor: '#f8fafc', borderRadius: 3, p: 4, border: '1px solid #e5e7eb' }}>
              <Typography sx={{ fontWeight: 600, color: '#111827', mb: 3, fontFamily: 'Inter, sans-serif' }}>
                What happens next?
              </Typography>
              
              {[
                { title: 'Email Invitation', desc: 'Users receive login credentials via email' },
                { title: 'Instant Access', desc: 'Users can sign in immediately' },
                { title: 'Manage Later', desc: 'You can add more users anytime' },
              ].map((item, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.5 }}>
                    <CheckIcon sx={{ fontSize: 14, color: '#22c55e' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 600, color: '#111827', fontSize: '0.875rem', fontFamily: 'Inter, sans-serif' }}>
                      {item.title}
                    </Typography>
                    <Typography sx={{ color: '#6b7280', fontSize: '0.8rem', fontFamily: 'Inter, sans-serif' }}>
                      {item.desc}
                    </Typography>
                  </Box>
                </Box>
              ))}

              <Box sx={{ mt: 4, pt: 4, borderTop: '1px solid #e5e7eb' }}>
                <Typography sx={{ color: '#6b7280', fontSize: '0.75rem', fontFamily: 'Inter, sans-serif' }}>
                  Organization
                </Typography>
                <Typography sx={{ fontWeight: 700, color: '#111827', fontFamily: 'Inter, sans-serif' }}>
                  {signupData.organization}
                </Typography>
                <Typography sx={{ color: '#6b7280', fontSize: '0.75rem', mt: 2, fontFamily: 'Inter, sans-serif' }}>
                  Plan
                </Typography>
                <Typography sx={{ fontWeight: 700, color: '#111827', fontFamily: 'Inter, sans-serif' }}>
                  {selectedPlan.name}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AddUsersSetup;
